import { ServerContext } from "@/context/Server";
import { usePathname } from "next/navigation";
import { useContext, useEffect, useState } from "react";
import { SONGLIST_WIDTH } from "./constants";
import type { SetListId, SongId } from "./types";

const useRouteInfo = () => {
  const pathName = usePathname() || "/";
  const pathNameSegments = pathName.slice(1).split("/");
  const isRouteHome = pathName === "/";
  const isRouteSongEditor = pathName.indexOf("/edit/") !== -1; // normal and set list song edit view
  const isRouteSongView = pathName.indexOf("/song/") !== -1; // normal and set list song view
  const isRouteSetList = pathName.indexOf("/setlist/") === 0;
  const isRouteSongPlay = pathName.indexOf("/play/") === 0;
  const isSongDemoView = pathName.indexOf("/song/demo/") === 0;

  let routerSongId: SongId = "";
  let routerSetListId: SetListId = "";
  if (isSongDemoView) {
    routerSongId = pathNameSegments[2] as SongId;
  } else if (isRouteSetList) {
    routerSetListId = pathNameSegments[1] as SetListId;
    if (pathNameSegments[3] === "song") {
      routerSongId = pathNameSegments[4] as SongId;
    }
  } else if (isRouteSongView || isRouteSongEditor || isRouteSongPlay) {
    routerSongId = pathNameSegments[1] as SongId;
  }

  return {
    isRouteHome,
    isRouteSetList,
    isRouteSongEditor,
    isRouteSongPlay,
    isRouteSongView,
    routerSongId,
    routerSetListId,
  };
};

const DESKTOP_BREAKPOINT = 1024;
const TABLET_BREAKPOINT = 648;
const useLayoutInfo = (): {
  isLayoutDesktop: boolean;
  isLayoutTablet: boolean;
  isLayoutPhone: boolean;
} => {
  const { isMobile } = useContext(ServerContext);
  const initialWidth =
    typeof document !== "undefined"
      ? document.documentElement.clientWidth
      : TABLET_BREAKPOINT + 1;

  const [width, setWidth] = useState<number>(initialWidth);

  function callbackSetWidth() {
    setWidth(document.documentElement.clientWidth);
  }

  useEffect(() => {
    callbackSetWidth();
    window.addEventListener("resize", callbackSetWidth);
    return () => {
      window.removeEventListener("resize", callbackSetWidth);
    };
  }, []);

  if (typeof document === "undefined") {
    if (isMobile) {
      return {
        isLayoutPhone: true,
        isLayoutTablet: false,
        isLayoutDesktop: false,
      };
    } else {
      return {
        isLayoutPhone: false,
        isLayoutTablet: true,
        isLayoutDesktop: false,
      };
    }
  }

  const isLayoutDesktop = width > DESKTOP_BREAKPOINT;
  const isLayoutTablet = width > TABLET_BREAKPOINT && !isLayoutDesktop;
  const isLayoutPhone = !isLayoutDesktop && !isLayoutTablet;

  return {
    isLayoutDesktop,
    isLayoutTablet,
    isLayoutPhone,
  };
};

export default function useInfo() {
  const {
    isRouteHome,
    isRouteSetList,
    isRouteSongEditor,
    isRouteSongPlay,
    isRouteSongView,
    routerSongId,
    routerSetListId,
  } = useRouteInfo();

  const { isLayoutDesktop, isLayoutTablet, isLayoutPhone } = useLayoutInfo();
  const songListWidth = isLayoutPhone ? undefined : SONGLIST_WIDTH;

  return {
    isLayoutDesktop,
    isLayoutTablet,
    isLayoutPhone,
    isRouteHome,
    isRouteSetList,
    isRouteSongEditor,
    isRouteSongPlay,
    isRouteSongView,
    routerSongId,
    routerSetListId,
    songListWidth,
  };
}
